import { MAX_COMMITMENT_YEARS, MAX_HORIZON_YEARS, MIN_COMMITMENT_YEARS } from "@shared/constants";
import { SelectField } from "@client/components/Field";
import { InfoTip } from "@client/components/InfoTip";
import { PercentField } from "@client/components/editor/NumberFields";
import { Panel } from "@client/components/Panel";
import { minHorizonFor, type DraftAction, type EditorModel } from "@client/lib/scenarioDraft";

/**
 * The four settings that apply to the whole scenario.
 *
 * The commitment picker carries its consequence in a sentence under it, because
 * "2" is not an answer to the question people are actually asking — "when can
 * these clients start leaving?" — and getting it wrong shifts every churn figure
 * in the projection by a year.
 */

function commitmentConsequence(commitmentYears: number): string {
  if (commitmentYears <= 0) {
    return "Clients can leave in the same year they sign.";
  }
  return `Clients signed in year 1 can first leave in year ${commitmentYears + 1}. Churn asked for before then is held back, and the projection says so.`;
}

interface Props {
  model: EditorModel;
  dispatch: (action: DraftAction) => void;
  disabled: boolean;
  /** Inside the rail's dialog, which carries the title and the card chrome. */
  bare?: boolean;
}

export function ScenarioSettingsPanel({ model, dispatch, disabled, bare = false }: Props) {
  // The commitment sets the floor — see `minHorizonFor`. `Math.min` with the
  // current value so a scenario already below its floor (one whose commitment
  // was raised after the fact) can still show what it holds rather than opening
  // on a dropdown with its own value missing.
  const horizonFloor = Math.min(minHorizonFor(model), model.horizonYears);
  const horizonOptions = Array.from(
    { length: MAX_HORIZON_YEARS - horizonFloor + 1 },
    (_, index) => horizonFloor + index,
  );
  const commitmentOptions = Array.from(
    { length: MAX_COMMITMENT_YEARS - MIN_COMMITMENT_YEARS + 1 },
    (_, index) => MIN_COMMITMENT_YEARS + index,
  );

  return (
    <Panel
      bare={bare}
      title={bare ? undefined : "Scenario settings"}
      description={
        bare ? undefined : "How far ahead this projection runs, and the rules for every package."
      }
      // The anchor the horizon-level warnings link to — see `warningTarget`.
      className="scroll-mt-4"
      id="scenario-settings"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="Horizon"
          hint="Adding years appends empty year rows; removing them drops the last rows and their adoption."
          value={model.horizonYears}
          disabled={disabled}
          onChange={(event) => dispatch({ type: "horizon", years: Number(event.target.value) })}
        >
          {horizonOptions.map((years) => (
            <option key={years} value={years}>
              {years} years
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Commitment"
          hint={commitmentConsequence(model.commitmentYears)}
          value={model.commitmentYears}
          disabled={disabled}
          onChange={(event) =>
            dispatch({ type: "settings", patch: { commitmentYears: Number(event.target.value) } })
          }
        >
          {commitmentOptions.map((years) => (
            <option key={years} value={years}>
              {years === 0 ? "None" : `${years} year${years === 1 ? "" : "s"}`}
            </option>
          ))}
        </SelectField>

        <PercentField
          label="Discount rate"
          value={model.discountRatePct}
          disabled={disabled}
          hint="Used for the net present value. Leave at 0 to compare plain cash."
          onChange={(discountRatePct) => dispatch({ type: "settings", patch: { discountRatePct } })}
        />

        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink">Assets</span>
          <div className="flex items-center gap-1.5">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={model.reuseAssets}
                disabled={disabled}
                onChange={(event) =>
                  dispatch({ type: "settings", patch: { reuseAssets: event.target.checked } })
                }
                className="h-4 w-4 shrink-0 accent-[var(--azure-deep)]"
              />
              Reuse hardware from leavers
            </label>
            <InfoTip label="reusing hardware">
              A churned client&rsquo;s unit returns to the pool and the next signing takes it
              instead of buying one. Off means every signing is a fresh purchase.
            </InfoTip>
          </div>
        </div>
      </div>

      <p className="flex items-center gap-1.5 text-xs text-ink-soft">
        Currency <span className="font-semibold text-ink">{model.currencyCode}</span>
        <InfoTip label="the currency">
          Set by the product, not the scenario, so every scenario on it can be compared.
        </InfoTip>
      </p>
    </Panel>
  );
}
